import { DeleteOutlined } from "@ant-design/icons";
import * as Sentry from "@sentry/browser";
import { Badge, Button, Card, Col, Collapse, message, Row, Spin } from "antd";
import { FormInstance } from "antd/lib/form";
import { AxiosResponse } from "axios";
import React, { Component, CSSProperties, Fragment, RefObject } from "react";
import {
  APIError,
  BankAccountTypesEnum,
  BranchMap,
  Company,
  CompanyPaymentAccount,
  DocumentTypes,
  FileTypes,
  FileUpload,
  FinancialInstitution,
  FormFieldConfig,
  Opportunity,
  PaymentAccount,
  PaymentAccountTypesEnum,
  ValidateAccount,
  UpdateDocumentsInformationFnProps,
  UpdatePaymentInformationFnProps,
} from "../helpers/shared-interfaces";
import {
  formatUploadedDocumentFileTypeLabel,
  generateDisplayErrorMessage,
  handleValidationErrors,
  mapAPIErrorToFormError,
  request,
  responseHandler,
} from "../helpers/utilities";
import entitlements from "../config/entitlements";
import {
  createPaymentAccountFormFields,
  PaymentAccountCardWrapper,
} from "./CustomerOnboardingDetails";
import BaseForm from "./Form/BaseForm";
import ModalCreator from "./ModalCreator";
import { DELETE_FILE } from "../gql/general";
import { VERIFY_PAYOUT_ACCOUNT } from "../gql/accounts";

const { Panel } = Collapse;

const CREATE_PAYOUT_ACCOUNT = `
mutation CreateCompanyPayoutAccount($accountNumber: String!, $accountType: String!, $bankBranchId: ID!, $companyId: ID!, $name: String! ) {
  createCompanyPayoutAccount(accountNumber: $accountNumber, accountType: $accountType, bankBranchId: $bankBranchId, companyId: $companyId, name: $name){
    companyPayoutAccount {
      id
      company {
        id
        companyPayoutAccounts {
          id
          name
          institutionName
          accountNumber
          accountType
          bankBranch {
            id
            name
            financialInstitution {
              id
              name
            }
          }
        }
      }
    }
    errors {
      property
      description
    }
  }
}`;

const UPDATE_PAYOUT_ACCOUNT = `
mutation UpdateCompanyPayoutAccount($id:ID!, $accountNumber: String!, $accountType: String!, $bankBranchId: ID!, $companyId: ID!, $name: String! ) {
  updateCompanyPayoutAccount(id: $id, accountNumber: $accountNumber, accountType: $accountType, bankBranchId: $bankBranchId, companyId: $companyId, name: $name){
    companyPayoutAccount {
      id
      company {
        id
        companyPayoutAccounts {
          id
          name
          institutionName
          accountNumber
          accountType
          bankBranch {
            id
            name
            financialInstitution {
              id
              name
            }
          }
        }
      }
    }
    errors {
      property
      description
    }
  }
}`;

const colStyle: CSSProperties = {
  paddingRight: "20px",
};

export enum Views {
  documentUploads = "Required Documents",
  payout = "Payout Account Information",
}

interface CustomCardProps<T> {
  type: string;
  toggleDeleteConfirmationModal?: (id?: string) => void;
  setEntityToBeDeleted?: (entity: string) => void;
  setIdToBeDeleted?: (id: string) => void;
  setCurrentlyEditing?: (type: string, object: T) => void;
  isEditing?: boolean;
}

interface UploadedDocumentCardProps
  extends CustomCardProps<Record<string, unknown>> {
  document: FileUpload;
}

interface CardCreatorProps {
  id: string;
  leftHandSideUI: JSX.Element;
  deleteBtn: JSX.Element;
  editButton?: JSX.Element;
  isEditing?: boolean;
  toggleDeleteConfirmationModal?: () => void
}

export function createDocumentUploadFormFields(props: {
  opportunity: Opportunity;
  company?: Company
  handleFileUploadSuccess: (value: string) => void;
  initialValues: UpdateDocumentsInformationFnProps;
}): FormFieldConfig[] {
  return [
    {
      id: "employeeDatasheetUrl",
      name: "employeeDatasheetUrl",
      inputType: "fileUpload",
      rule: "string",
      initialValue: props.initialValues.employeeDatasheet?.url,
      required: false,
      disabled: false,
      label: "Employee Datasheet URL (EDS)",
      fileUploadProps: {
        type: FileTypes.employeeDatasheet,
        opportunityId: props.opportunity.id,
        companyId: props.company?.id,
        skipJobProcess: false,
      },
      changeHandler: props.handleFileUploadSuccess,
      colConfig: {
        span: 17,
      },
    },

    {
      id: "signedApplicationFormUrl",
      name: "signedApplicationFormUrl",
      inputType: "fileUpload",
      rule: "string",
      initialValue: props.initialValues.signedApplicationForm?.url,
      required: false,
      disabled: false,
      label: "Signed Application Form",
      fileUploadProps: {
        type: FileTypes.signedApplicationForm,
        opportunityId: props.opportunity.id,
        companyId: props.company?.id,
        skipJobProcess: true,
      },
      colConfig: {
        span: 17
      },
      changeHandler: props.handleFileUploadSuccess,
    },
    {
      id: "npraFormUrl",
      name: "npraFormUrl",
      inputType: "fileUpload",
      initialValue: props.initialValues.npraForm?.url,
      rule: "string",
      required: false,
      disabled: false,
      label: "NPRA Form",
      fileUploadProps: {
        type: FileTypes.npraForm,
        opportunityId: props.opportunity.id,
        companyId: props.company?.id,
        skipJobProcess: true,
      },
      colConfig: {
        span: 17,
      },
      changeHandler: props.handleFileUploadSuccess,
    },
    {
      id: "vestingRuleDocumentUrl",
      name: "vestingRuleDocumentUrl",
      inputType: "fileUpload",
      initialValue: props.initialValues.vestingRuleDocument?.url,
      rule: "string",
      required: false,
      disabled: false,
      label: "Vesting Rule Document",
      fileUploadProps: {
        type: FileTypes.vestingRuleDocument,
        opportunityId: props.opportunity.id,
        companyId: props.company?.id,
        skipJobProcess: false,
      },
      colConfig: {
        span: 17,
      },
      changeHandler: props.handleFileUploadSuccess,
    },
  ];
}

export function CardCreator(props: CardCreatorProps): JSX.Element {
  return (
    <Card
      className="associated-entity-card"
      style={{ marginBottom: "10px" }}
      key={props.id}
    >
      <Row>
        <Col
          span={11}
          style={{
            marginTop: "11px",
          }}
        >
          {props.leftHandSideUI}
        </Col>
        <Col
          span={13}
          style={{
            textAlign: "right",
          }}
        >
          {props.isEditing && props.editButton}
          {props.isEditing && props.deleteBtn}
        </Col>
      </Row>
    </Card>
  );
}

export const UploadedDocumentCard = function (
  props: UploadedDocumentCardProps,
): JSX.Element {
  return CardCreator({
    id: props.document.id,
    isEditing: props.isEditing,
    leftHandSideUI: (
      <div>
        <h3>{formatUploadedDocumentFileTypeLabel(props.document.fileType)}</h3>
        <a target="_blank" href={props.document.url} rel="noopener noreferrer">
          View File
        </a>
      </div>
    ),
    deleteBtn: (
      <Button
        id="uploaded-document-card-delete-btn"
        style={{ marginRight: "9px", marginTop: "6px" }}
        onClick={(): void => {
          props.setEntityToBeDeleted?.(props.document.fileType ?? "");
          props.toggleDeleteConfirmationModal?.(props.document.id);
        }}
        danger
      >
        <DeleteOutlined /> 
      </Button>
    ),
  });
};

export const createUploadedDocumentObject = function (
  props: UpdateDocumentsInformationFnProps,
): FileUpload[] {
  const response: FileUpload[] = [];

  if (props.employeeDatasheet) {
    response.push({
      id: DocumentTypes.eds,
      fileType: DocumentTypes.eds,
      url: props.employeeDatasheet.url,
    });
  }

  if (props.signedApplicationForm) {
    response.push({
      id: DocumentTypes.signedApplicationForm,
      fileType: DocumentTypes.signedApplicationForm,
      url: props.signedApplicationForm.url,
    });
  }

  if (props.npraForm) {
    response.push({
      id: DocumentTypes.npraForm,
      fileType: DocumentTypes.npraForm,
      url: props.npraForm.url,
    });
  }

  if (props.vestingRuleDocument) {
    response.push({
      id: DocumentTypes.vestingRuleDocument,
      fileType: DocumentTypes.vestingRuleDocument,
      url: props.vestingRuleDocument.url,
    });
  }

  if (props.companyInfoFileUploads) {
    props.companyInfoFileUploads.forEach(fileUpload => {
      response.push({
        id: fileUpload.id,
        fileType: fileUpload.fileType,
        url: fileUpload.url,
      });
    });
  }

  return response;
};

export function ModalToggler({
  modalTarget,
  showModal,
  toggleModal,
  deleteSignedApplicationForm,
  deleteVestingRuleDocument,
  deleteEmployeeDatasheet,
  deleteNPRAForm,
  deleteCompanyDocument
}: {
  modalTarget: string | null;
  showModal: boolean;

  deleteSignedApplicationForm: () => void;
  deleteVestingRuleDocument: () => void;
  deleteEmployeeDatasheet: () => void;
  deleteNPRAForm: () => void;
  deleteCompanyDocument: () => void;

  toggleModal: () => void;
}): JSX.Element {
  let modal = <div />;

  switch (modalTarget) {
    case DocumentTypes.signedApplicationForm:
      modal = ModalCreator({
        showModal,
        onOkay: deleteSignedApplicationForm,
        toggleModal: toggleModal,
        inscription: "Are you sure you want to delete this document?",
      });
      break;
    case DocumentTypes.eds:
      modal = ModalCreator({
        showModal,
        onOkay: deleteEmployeeDatasheet,
        toggleModal: toggleModal,
        inscription: "Are you sure you want to delete this document?",
      });
      break;
    case DocumentTypes.npraForm:
      modal = ModalCreator({
        showModal,
        onOkay: deleteNPRAForm,
        toggleModal: toggleModal,
        inscription: "Are you sure you want to delete this document?",
      });
      break;
    case DocumentTypes.vestingRuleDocument:
      modal = ModalCreator({
        showModal,
        onOkay: deleteVestingRuleDocument,
        toggleModal: toggleModal,
        inscription: "Are you sure you want to delete this document?",
      });
      break;
    default:
      modal = ModalCreator({
        showModal,
        onOkay: deleteCompanyDocument,
        toggleModal: toggleModal,
        inscription: "Are you sure you want to delete this document?",
      })
  }
  return modal;
}

export function AssociatedEntityListCreator({
  toggleDeleteConfirmationModal,
  setIdToBeDeleted,
  setEntityToBeDeleted,
  isEditing,
  setCurrentlyEditing,
  entitlement,
}: {
  toggleDeleteConfirmationModal: (id?: string) => void;
  setIdToBeDeleted: (id: string) => void;
  setEntityToBeDeleted: (id: string) => void;
  isEditing?: boolean;
  setCurrentlyEditing?: (type: string, obj: PaymentAccount) => void;
  entitlement?: string;
}): ({
  entity,
  array,
}: {
  entity: string;
  array: PaymentAccount[] | FileUpload[];
}) => JSX.Element[] {
  return ({
    entity,
    array,
  }: {
    entity: string;
    array: PaymentAccount[] | FileUpload[];
  }): JSX.Element[] => {
    const elements = [];
    switch (entity) {
      case Views.payout:
        for (const item of array) {
          elements.push(
            PaymentAccountCardWrapper({
              isEditing,
              paymentAccount: item as PaymentAccount,
              setEntityToBeDeleted,
              toggleDeleteConfirmationModal,
              setCurrentlyEditing,
              setIdToBeDeleted,
              type: Views.payout,
              entitlement,
            }),
          );
        }
        break;
      case Views.documentUploads: {
        const docCols: JSX.Element[] = [];
        for (const item of array) {
          const doc = item as FileUpload;
          docCols.push(
            <Col key={doc.id} xs={24} sm={12} md={8} lg={8} xl={8}>
              {UploadedDocumentCard({
                document: doc,
                setEntityToBeDeleted,
                toggleDeleteConfirmationModal: () =>
                  toggleDeleteConfirmationModal(item?.id),
                type: doc.fileType ?? "",
                setIdToBeDeleted,
                isEditing: true,
              })}
            </Col>,
          );
        }
        elements.push(
          <Row gutter={[16, 16]} key="document-uploads-row">
            {docCols}
          </Row>,
        );
        break;
      }
    }
    return elements;
  };
}

interface Props {
  activekey?: string;
  opportunity: Opportunity;
  company?: Company
  branchMap: BranchMap;
  loadOpportunity: () => void;
  isEditing: boolean;
  financialInstitutions: FinancialInstitution[];
  updateCompanyPaymentInformation: (
    props: UpdatePaymentInformationFnProps,
  ) => void;
  visibleViews?: string[];
  showList?: boolean;
  renderInline?: boolean;
  showInlineHeader?: boolean;
  hideFormOnDelete?: boolean;
  existingDocumentFileTypes?: string[];
  onCancelEdit?: () => void;
}

interface State {
  id: string;
  modalTarget: string;
  idToBeDeleted?: string;
  showDeleteConfirmationModal: boolean;
  isLoading: boolean;
  payoutFinancialInstitutionId: string;
  paymentAccountType: string;
  payoutAccountBeingEdited?: PaymentAccount;
  debitAccountBeingEdited?: PaymentAccount;
  currentType: string;
  accountValidationResponse?: ValidateAccount;
}

export default class CompanyOnboardingDetails extends Component<Props, State> {
  docsReference: RefObject<FormInstance>;
  payoutReference: RefObject<FormInstance>;

  constructor(props: Props) {
    super(props);
    this.state = {
      id: "",
      payoutFinancialInstitutionId: "",
      modalTarget: "",
      isLoading: false,
      showDeleteConfirmationModal: false,
      paymentAccountType: PaymentAccountTypesEnum.bankAccount,
      currentType: "",
    };
    this.payoutReference = React.createRef<FormInstance>();
    this.docsReference = React.createRef<FormInstance>();
  }

  handleFileUploadSuccess = (): void => {
    this.props.loadOpportunity();
  };

  setRef = (
    name: "docsReference" | "payoutReference",
    ref: React.RefObject<FormInstance>,
  ): void => {
    this[name] = ref;
  };

  setEntityToBeDeleted = (modalTarget: string): void => {
    this.setState({ modalTarget });
  };

  toggleDeleteConfirmationModal = (id?: string): void => {
    this.setState((prevState => ({
      showDeleteConfirmationModal: !prevState.showDeleteConfirmationModal,
      idToBeDeleted: id
    })))
  };

  setIdToBeDeleted = (idToBeDeleted: string): void => {
    this.setState({ idToBeDeleted });
  };

  deleteFile = async (id: string): Promise<void> => {
    interface Response {
      result: boolean;
      errors: APIError[];
    }

    this.setState({
      isLoading: true,
    });

    try {
      const response: AxiosResponse = await request({
        query: DELETE_FILE,
        variables: {
          id,
        },
      });

      const { mutation } = responseHandler<Response>(
        response,
        "deleteFileUpload",
      );

      if (mutation.result) {
        message.success("File deleted successfully")
        await this.props.loadOpportunity();
      } else {
        message.error("Failed to delete file");
      }
    } catch (error) {
      message.error(`Failed to delete a file - ${generateDisplayErrorMessage(error)}`);
      Sentry.captureException(error);
    } finally {
      this.setState({ isLoading: false });
      this.toggleDeleteConfirmationModal()
    }
  };

  deleteSignedApplicationForm = async (): Promise<void> => {
    this.toggleDeleteConfirmationModal();
    this.props.opportunity.signedApplicationForm &&
      (await this.deleteFile(this.props.opportunity.signedApplicationForm.id));
  };

  deleteVestingRuleDocument = async (): Promise<void> => {
    this.toggleDeleteConfirmationModal();
    this.props.opportunity.vestingRuleDocument &&
      (await this.deleteFile(this.props.opportunity.vestingRuleDocument.id));
  };

  deleteEmployeeDatasheet = async (): Promise<void> => {
    this.toggleDeleteConfirmationModal();
    this.props.opportunity.employeeDatasheet &&
      (await this.deleteFile(this.props.opportunity.employeeDatasheet.id));
  };

  deleteNPRAForm = async (): Promise<void> => {
    this.toggleDeleteConfirmationModal();
    this.props.opportunity.npraForm &&
      (await this.deleteFile(this.props.opportunity.npraForm.id));
  };

  deleteCompanyDocument = async (): Promise<void> => {
    this.props.company?.companyInfoFileUploads &&
      (await this.deleteFile(this.state.idToBeDeleted ?? ""))
  }

  generateTabMetaData = (item: string, length: number): JSX.Element => {
    const badgeStyle: CSSProperties = {
      backgroundColor: "#52c41a",
      marginLeft: "12px",
    };
    return (
      <span>
        {item} <Badge count={length} style={badgeStyle} />
      </span>
    );
  };

  handlePayoutFinancialInstitutionChange = (
    payoutFinancialInstitutionId: string,
  ): void => {
    this.setState({ payoutFinancialInstitutionId });
  };

  handlePaymentTypeChange = (paymentAccountType: string): void => {
    this.setState({ paymentAccountType });
  };

  resetState = (): void => {
    this.setState(
      {
        payoutFinancialInstitutionId: "",
        payoutAccountBeingEdited: undefined,
        paymentAccountType: PaymentAccountTypesEnum.bankAccount,
        debitAccountBeingEdited: undefined,
        currentType: "",
        accountValidationResponse: undefined,
      },
      () => {
        this.payoutReference.current?.resetFields();
      },
    );
  };

  verifyAccount = async (): Promise<void> => {
    interface Response {
      data: {
        validatePayoutAccount: {
          status: boolean;
          name: string;
          message: string;
        };
      };
    }

    const fieldsToBeValidated = ["financialInstitutionId", "accountNumber"];

    if (!this.payoutReference.current) return;

    let data;

    try {
      data = await this.payoutReference.current.validateFields(fieldsToBeValidated);
    } catch (error) {
      handleValidationErrors(error);
      return;
    }

    this.setState({ isLoading: true });

    try {
      await request({
        query: VERIFY_PAYOUT_ACCOUNT,
        variables: {
          ...data,
        },
      }).then(({ data }: AxiosResponse<Response>): void => {
        const response = data.data.validatePayoutAccount;
        const { status, name, message: responseMessage } = response;

        if (status) {
          this.setState({
            accountValidationResponse: {
              status,
              name,
              message: responseMessage,
              score: 100,
            },
          });
          message.success("Account verified successfully");
          message.success(`Account name: ${name}`);
        } else {
          this.setState({ accountValidationResponse: undefined });
          message.error(responseMessage || "Error verifying account number");
        }
      });
    } catch (error) {
      message.error("Error verifying account number");
      Sentry.captureException(error);
    } finally {
      this.setState({ isLoading: false });
    }
  };

  createDataBasedOnPayoutType = (
    data: Record<string, unknown>,
    financialInstitutionFor: "payoutFinancialInstitutionId",
  ): Record<string, unknown> => {
    if (this.state.paymentAccountType === PaymentAccountTypesEnum.momoAccount) {
      return {
        ...data,
        bankAccountType: BankAccountTypesEnum.currentAccount,
        accountNumber: data.phoneNumber,
        bankBranchId:
          this.props.branchMap[this.state[financialInstitutionFor]][0]?.id,
      };
    }
    return data;
  };

  createPayoutAccount = async (): Promise<void> => {
    interface Response {
      companyPayoutAccount: CompanyPaymentAccount;
      errors: APIError[];
    }

    const fieldsToBeValidated = [
      "accountType",
      "financialInstitutionId",
      "bankBranchId",
      "phoneNumber",
      "accountNumber",
      "accountType",
      "name",
    ];

    if (!this.payoutReference.current) return;

    let data;
    const accountName =
      this.state.accountValidationResponse?.name ||
      this.props.company?.name ||
      this.props.opportunity.company?.name ||
      "";

    try {
      data = await this.payoutReference.current.validateFields(
        fieldsToBeValidated,
      );
    } catch (error) {
      handleValidationErrors(error);
      return;
    }

    this.setState({ isLoading: true });

    try {
      const response: AxiosResponse = await request({
        query: CREATE_PAYOUT_ACCOUNT,
        variables: {
          ...this.createDataBasedOnPayoutType(
            data,
            "payoutFinancialInstitutionId",
          ),
          companyId: this.props.opportunity.company?.id,
          name: accountName,
        },
      });

      const { err, mutation } = responseHandler<Response>(
        response,
        "createCompanyPayoutAccount",
      );

      if (err) {
        this.payoutReference.current.setFields(err.map(mapAPIErrorToFormError));
      } else {
        this.payoutReference.current.resetFields();
        mutation.companyPayoutAccount &&
          this.props.updateCompanyPaymentInformation({
            companyPayoutAccounts:
              mutation.companyPayoutAccount.company.companyPayoutAccounts,
          });
        this.props.loadOpportunity();
        this.props.onCancelEdit?.();
      }
    } catch (error) {
      message.error(`failed to create a payout account - ${generateDisplayErrorMessage(error)}`);
      Sentry.captureException(error);
    }

    this.setState({
      isLoading: false,
    });

    this.resetState();
  };

  updatePayoutAccount = async (): Promise<void> => {
    interface Response {
      companyPayoutAccount: CompanyPaymentAccount;
      errors: APIError[];
    }

    const fieldsToBeValidated = [
      "accountType",
      "financialInstitutionId",
      "bankBranchId",
      "phoneNumber",
      "accountNumber",
      "accountType",
      "name",
    ];

    if (!this.payoutReference.current) return;

    let data;
    const accountName =
      this.state.accountValidationResponse?.name ||
      this.props.company?.name ||
      this.props.opportunity.company?.name ||
      "";

    try {
      data = await this.payoutReference.current.validateFields(
        fieldsToBeValidated,
      );
    } catch (error) {
      handleValidationErrors(error);
      return;
    }

    this.setState({ isLoading: true });

    try {
      const response: AxiosResponse = await request({
        query: UPDATE_PAYOUT_ACCOUNT,
        variables: {
          ...this.createDataBasedOnPayoutType(
            data,
            "payoutFinancialInstitutionId",
          ),
          id: this.state.payoutAccountBeingEdited?.id,
          companyId: this.props.opportunity.company?.id,
          name: accountName,
        },
      });

      const { err, mutation } = responseHandler<Response>(
        response,
        "updateCompanyPayoutAccount",
      );

      if (err) {
        this.payoutReference.current.setFields(err.map(mapAPIErrorToFormError));
      } else {
        this.payoutReference.current.resetFields();
        mutation.companyPayoutAccount &&
          this.props.updateCompanyPaymentInformation({
            companyPayoutAccounts:
              mutation.companyPayoutAccount.company.companyPayoutAccounts,
          });
        this.props.loadOpportunity();
        this.props.onCancelEdit?.();
      }
    } catch (error) {
      message.error(`failed to create a update account - ${generateDisplayErrorMessage(error)}`);
      Sentry.captureException(error);
    }

    this.setState({
      isLoading: false,
    });

    this.resetState();
  };

  createDebitAccount = async (): Promise<void> => {
    // Placeholder for debit account creation
    message.info("Debit account creation not implemented for company");
    this.resetState();
  };

  updateDebitAccount = async (): Promise<void> => {
    // Placeholder for debit account update
    message.info("Debit account update not implemented for company");
    this.resetState();
  };

  setCurrentlyEditing = (type: string, p?: PaymentAccount): void => {
    if (p) {
      const financialInstitutionId = p.bankBranch?.financialInstitution
        .id as string;
      const paymentAccountType = p.accountType as string;

      if (type === "debit") {
        this.setState({
          payoutFinancialInstitutionId: financialInstitutionId,
          debitAccountBeingEdited: p,
          paymentAccountType,
          currentType: type,
        });
      } else {
        this.setState({
          payoutFinancialInstitutionId: financialInstitutionId,
          payoutAccountBeingEdited: p,
          paymentAccountType,
          currentType: type,
        });
      }
    } else {
      this.setState({
        currentType: type,
      });
    }
  };

  render(): JSX.Element | null {
    const {
      isLoading,
      showDeleteConfirmationModal,
      modalTarget,
      payoutAccountBeingEdited,
    } = this.state;
    const { opportunity, isEditing, company } = this.props;

    if (!company) {
      return null;
    }

    const transformedUploadedDocuments = createUploadedDocumentObject({
      signedApplicationForm: opportunity.signedApplicationForm,
      employeeDatasheet: opportunity.employeeDatasheet,
      npraForm: opportunity.npraForm,
      vestingRuleDocument: opportunity.vestingRuleDocument,
      companyInfoFileUploads: company?.companyInfoFileUploads
    });

    const documentUploadsFields: FormFieldConfig[] =
      createDocumentUploadFormFields({
        opportunity: this.props.opportunity,
        company: this.props.company,
        handleFileUploadSuccess: this.handleFileUploadSuccess,
        initialValues: {
          npraForm: opportunity.npraForm,
          vestingRuleDocument: opportunity.vestingRuleDocument,
          employeeDatasheet: opportunity.employeeDatasheet,
          signedApplicationForm: opportunity.signedApplicationForm,
        },
      });

    const entityListCreator = AssociatedEntityListCreator({
      toggleDeleteConfirmationModal: this.toggleDeleteConfirmationModal,
      setEntityToBeDeleted: this.setEntityToBeDeleted,
      setIdToBeDeleted: this.setIdToBeDeleted,
      setCurrentlyEditing: this.setCurrentlyEditing,
      isEditing,
      entitlement: entitlements.Companies.edit,
    });

    const arrayOfViews =
      this.props.visibleViews && this.props.visibleViews.length > 0
        ? this.props.visibleViews
        : [Views.payout, Views.documentUploads];

    const showList = this.props.showList !== false;
    const renderInline = this.props.renderInline === true;
    const showInlineHeader = this.props.showInlineHeader === true;
    const suppressForm =
      this.props.hideFormOnDelete === true && showDeleteConfirmationModal;

    let institutions: FinancialInstitution[] = [];
    if (this.state.paymentAccountType === "mm_account") {
      institutions = this.props.financialInstitutions.filter(
        (institution: FinancialInstitution): boolean => {
          return institution.institutionType === "mobile_money";
        },
      );
    } else {
      institutions = this.props.financialInstitutions.filter(
        (institution: FinancialInstitution): boolean => {
          return institution.institutionType === "bank";
        },
      );
    }

    const payoutBranch =
      this.props.branchMap[this.state.payoutFinancialInstitutionId];

    const payoutFields: FormFieldConfig[] = createPaymentAccountFormFields({
      createPaymentAccount: this.createPayoutAccount,
      updatePaymentAccount: this.updatePayoutAccount,
      initialValue: payoutAccountBeingEdited,
      financialInstitutions: institutions,
      isEditing,
      name: company.name,
      handleAccountTypeSelect: this.handlePaymentTypeChange,
      handleAccountTypeChange: this.handlePaymentTypeChange,
      handleFinancialInstitutionChange:
        this.handlePayoutFinancialInstitutionChange,
      paymentAccountType: this.state.paymentAccountType,
      branches: payoutBranch,
      institutions: institutions,
      accountValidationResponse: this.state.accountValidationResponse,
      verifyAccount: this.verifyAccount,
    });

    const debitFields: FormFieldConfig[] = createPaymentAccountFormFields({
      createPaymentAccount: this.createDebitAccount,
      updatePaymentAccount: this.updateDebitAccount,
      initialValue: this.state.debitAccountBeingEdited,
      financialInstitutions: institutions,
      isEditing: !!this.state.debitAccountBeingEdited,
      name: company.name,
      handleAccountTypeSelect: this.handlePaymentTypeChange,
      handleAccountTypeChange: this.handlePaymentTypeChange,
      handleFinancialInstitutionChange:
        this.handlePayoutFinancialInstitutionChange,
      paymentAccountType: this.state.paymentAccountType,
      branches: payoutBranch,
      institutions: institutions,
      accountValidationResponse: this.state.accountValidationResponse,
      verifyAccount: this.verifyAccount,
      isDebit: true,
    });

    let paymentAccountFieldsToHide: string[] = [];

    if (this.state.paymentAccountType === PaymentAccountTypesEnum.bankAccount) {
      paymentAccountFieldsToHide = ["phoneNumber", "bankAccountType"];
    } else {
      paymentAccountFieldsToHide = [
        "accountNumber",
        "bankAccountType",
        "bankBranchId",
      ];
    }

    const isDebitMode = this.state.currentType === "debit";
    const debitOnlyFieldsToHide = isDebitMode
      ? []
      : ["authorizationFormUrl", "expirationDate", "amount"];

    return (
      <div style={{ width: "100%" }}>
        <Fragment>
          {ModalToggler({
            toggleModal: this.toggleDeleteConfirmationModal,
            showModal: showDeleteConfirmationModal,
            modalTarget: modalTarget,
            deleteEmployeeDatasheet: this.deleteEmployeeDatasheet,
            deleteNPRAForm: this.deleteNPRAForm,
            deleteVestingRuleDocument: this.deleteVestingRuleDocument,
            deleteSignedApplicationForm: this.deleteSignedApplicationForm,
            deleteCompanyDocument: this.deleteCompanyDocument
          })}
          {renderInline ? (
            <div>
              {arrayOfViews.map((item: string): JSX.Element => {
                let array = [];
                let form: JSX.Element | null = null;

                if (item === Views.payout) {
                  array = company.companyPayoutAccounts;
                  form = (
                    <BaseForm
                      reference={this.payoutReference}
                      fields={this.state.currentType === "debit" ? debitFields : payoutFields}
                      layout="vertical"
                      fieldsToHide={[
                        ...paymentAccountFieldsToHide,
                        ...debitOnlyFieldsToHide,
                      ]}
                    />
                  );
                } else {
                  array = transformedUploadedDocuments;
                  form = (
                    <BaseForm
                      reference={this.docsReference}
                      fields={documentUploadsFields}
                      layout="vertical"
                      fieldsToHide={[]}
                    />
                  );
                }

                if (suppressForm) return <div key={item} />;

                return (
                  <div key={item}>
                    {showInlineHeader && (
                      <div style={{ marginBottom: 12 }}>
                        {this.generateTabMetaData(item, array.length)}
                      </div>
                    )}
                    {form}
                  </div>
                );
              })}
            </div>
          ) : (
            <Collapse>
              {arrayOfViews.map((item: string): JSX.Element => {
                let array = [];
                let listCreator;
                let form;

                if (item === Views.payout) {
                  form = (
                    <BaseForm
                      reference={this.payoutReference}
                      fields={payoutFields}
                      layout="vertical"
                      fieldsToHide={[
                        ...paymentAccountFieldsToHide,
                        "authorizationFormUrl",
                        "expirationDate",
                        "amount",
                      ]}
                    />
                  );
                  listCreator = entityListCreator({
                    entity: Views.payout,
                    array: company.companyPayoutAccounts,
                  });
                  array = company.companyPayoutAccounts;
                } else {
                  form = (
                    <BaseForm
                      reference={this.docsReference}
                      fields={documentUploadsFields}
                      layout="vertical"
                      fieldsToHide={[]}
                    />
                  );
                  listCreator = entityListCreator({
                    entity: Views.documentUploads,
                    array: transformedUploadedDocuments,
                  });
                  array = transformedUploadedDocuments;
                }

                return (
                  <Panel
                    header={this.generateTabMetaData(item, array.length)}
                    key={item}
                  >
                    <Spin spinning={isLoading}>
                      <Row>
                        <Col span={showList ? 11 : 24} style={colStyle}>
                          {suppressForm ? null : form}
                        </Col>

                        {showList && (
                          <Col offset={1} span={12}>
                            {listCreator}
                          </Col>
                        )}
                      </Row>
                    </Spin>
                  </Panel>
                );
              })}
            </Collapse>
          )}
        </Fragment>
      </div>
    );
  }
}
